import { useEffect, useState } from "react";
import { Link } from "wouter";
import { GraduationCap, CheckCircle2, Loader2, Award } from "lucide-react";
import { toast } from "sonner";
import {
  getFormationProgression,
  validerModule,
  type FormationProgression,
  type Parcours,
} from "@/lib/formationApi";
import {
  CURRICULA_CONTENU,
  calculerScore,
  type ModuleContenu,
} from "@/lib/formationContent";

const GOLD = "oklch(0.695 0.105 68)";
const GRAPHITE = "oklch(0.18 0.02 243)";
const BORDER = "color-mix(in oklch, oklch(0.695 0.105 68) 18%, transparent)";

function estValide(p: FormationProgression | null, id: string): boolean {
  return Boolean(p?.modules.find((m) => m.module_id === id)?.valide);
}

export default function Formation({
  parcours = "AVOCAT",
}: {
  parcours?: Parcours;
}) {
  const [prog, setProg] = useState<FormationProgression | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reponses, setReponses] = useState<Record<string, number[]>>({});
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getFormationProgression(parcours)
      .then((p) => {
        if (!cancelled) setProg(p);
      })
      .catch(() => {
        if (!cancelled) setError("Formation indisponible (connexion requise).");
      });
    return () => {
      cancelled = true;
    };
  }, [parcours]);

  const setReponse = (moduleId: string, qIdx: number, optIdx: number) => {
    setReponses((prev) => {
      const arr = [...(prev[moduleId] ?? [])];
      arr[qIdx] = optIdx;
      return { ...prev, [moduleId]: arr };
    });
  };

  const onValider = async (m: ModuleContenu) => {
    setBusy(m.id);
    try {
      let score: number | undefined;
      if (m.scoreMax !== null) {
        const ans = reponses[m.id] ?? [];
        if (ans.length < m.qcm.length || ans.includes(undefined as never)) {
          toast.error("Répondez à toutes les questions.");
          setBusy(null);
          return;
        }
        score = calculerScore(m, ans);
      }
      const updated = await validerModule(m.id, score, parcours);
      setProg(updated);
      toast.success(`${m.titre} validé.`);
    } catch {
      toast.error(
        m.seuil !== null
          ? `Score insuffisant (seuil ${m.seuil}/${m.scoreMax}). Réessayez.`
          : "Échec de la validation.",
      );
    } finally {
      setBusy(null);
    }
  };

  const modules = CURRICULA_CONTENU[parcours] ?? CURRICULA_CONTENU.AVOCAT;
  const statut = prog?.statut_certification ?? "EN_COURS";
  const certifie = statut === "CERTIFIE" || statut === "EXPERT";
  const retour = parcours === "ARBITRE" ? "/dashboard" : "/avocat";

  return (
    <div
      className="min-h-screen px-4 py-6"
      style={{ background: GRAPHITE, color: "oklch(0.95 0.01 243)" }}
    >
      <div className="mx-auto max-w-3xl space-y-5">
        <header className="flex items-center gap-2">
          <GraduationCap size={20} style={{ color: GOLD }} />
          <h1 className="text-lg font-semibold">
            Formation certifiante — {parcours === "ARBITRE" ? "Arbitre" : "Avocat"}
          </h1>
          <Link
            href={retour}
            className="ml-auto text-xs underline"
            style={{ color: GOLD }}
          >
            ← Retour
          </Link>
        </header>

        <div
          className="rounded-md border p-3 flex items-center gap-2"
          style={{ borderColor: BORDER }}
        >
          {certifie ? (
            <Award size={18} style={{ color: GOLD }} />
          ) : (
            <GraduationCap size={18} style={{ color: GOLD }} />
          )}
          <span className="text-sm">
            Statut : <strong>{statut}</strong>
            {prog?.badge_code ? ` — ${prog.badge_code}` : ""}
          </span>
        </div>

        {error && (
          <div className="rounded-md border p-3 text-sm" style={{ borderColor: BORDER }}>
            {error}
          </div>
        )}

        {!prog && !error && (
          <div className="flex items-center gap-2 text-sm" style={{ opacity: 0.7 }}>
            <Loader2 size={14} className="animate-spin" /> Chargement…
          </div>
        )}

        {prog &&
          modules.map((m) => {
            const valide = estValide(prog, m.id);
            return (
              <section
                key={m.id}
                className="rounded-md border p-4 space-y-3"
                style={{ borderColor: BORDER }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">{m.titre}</span>
                  {valide && (
                    <CheckCircle2 size={16} style={{ color: GOLD }} className="ml-auto" />
                  )}
                </div>

                <ul className="list-disc pl-5 text-[13px] space-y-0.5" style={{ opacity: 0.85 }}>
                  {m.points.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>

                {!valide && m.qcm.length > 0 && (
                  <div className="space-y-3">
                    {m.qcm.map((q, qi) => (
                      <div key={qi} className="space-y-1">
                        <div className="text-[13px] font-medium">
                          {qi + 1}. {q.question}
                        </div>
                        <div className="space-y-1">
                          {q.options.map((opt, oi) => (
                            <label
                              key={oi}
                              className="flex items-center gap-2 text-[13px] cursor-pointer"
                            >
                              <input
                                type="radio"
                                name={`${m.id}-${qi}`}
                                checked={(reponses[m.id] ?? [])[qi] === oi}
                                onChange={() => setReponse(m.id, qi, oi)}
                              />
                              {opt}
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!valide && (
                  <button
                    type="button"
                    disabled={busy === m.id}
                    onClick={() => onValider(m)}
                    className="rounded-md px-3 py-1.5 text-sm font-medium disabled:opacity-50"
                    style={{ background: GOLD, color: GRAPHITE }}
                  >
                    {busy === m.id
                      ? "…"
                      : m.qcm.length > 0
                        ? "Soumettre le QCM"
                        : "Valider le module"}
                  </button>
                )}
              </section>
            );
          })}
      </div>
    </div>
  );
}
